import {
  AgentChatMessage,
  AgentRun,
} from '../components/agentChat/AgentChat.types';
import { config } from '../config';
import { AppIdentifiers } from '../store/slices/token-state.slice';
import { getAppStore } from '../store/storeRef';
import { readUserLabel } from '../utils/userLabel';

/**
 * Ships settled Ask Crate Hackers conversations to the chat-logs Supabase so
 * the team can review them on the chat-admin dashboard. Writes use the anon
 * key with insert/update-only policies; the dashboard reads with the service
 * role key. Silently off when the env keys are absent (e.g. local dev).
 */

export type ChatRating = 'up' | 'down';

const SETTLED = new Set(['completed', 'failed', 'cancelled']);
const STATE_KEY = 'chat-telemetry-state';
const STATE_LIMIT = 100;

export const isChatTelemetryConfigured = (): boolean =>
  !!(config.CHAT_LOGS_URL && config.CHAT_LOGS_KEY);

/** The reviewable conversation: what the DJ asked and what the app answered. */
export const transcriptOf = (
  messages: AgentChatMessage[],
): AgentChatMessage[] =>
  messages.filter(
    (message) =>
      (message.role === 'user' || message.role === 'agent') &&
      (message.content.trim() || message.attachments?.length),
  );

/** Message text with screenshot bytes replaced by a marker. */
export const telemetryContent = (message: AgentChatMessage): string => {
  const count = message.attachments?.length ?? 0;
  if (!count) return message.content;
  const marker = `[${count} screenshot${count === 1 ? '' : 's'}]`;
  return message.content.trim() ? `${message.content}\n${marker}` : marker;
};

const CONFUSED_PATTERN =
  /i can(?:no|')t|i cannot|i don'?t know|i'?m not sure|not sure how|unable to|can'?t help|cannot help|don'?t have a way|no way to do/i;
const ERROR_PATTERN = /^run error|hit the length limit|\bfailed: /im;

/** Triage flags the dashboard alerts on. */
export const computeChatFlags = (
  status: string,
  error: string | null | undefined,
  transcript: AgentChatMessage[],
): string[] => {
  const flags: string[] = [];
  if (status === 'failed' || error) flags.push('failed');
  const replies = transcript.filter((message) => message.role === 'agent');
  if (replies.some((message) => ERROR_PATTERN.test(message.content))) {
    flags.push('error');
  }
  if (replies.some((message) => CONFUSED_PATTERN.test(message.content))) {
    flags.push('confused');
  }
  return flags;
};

const identity = (): { email: string; name: string } => {
  const email = localStorage.getItem('email') || '';
  const token =
    getAppStore().getState().tokenState[AppIdentifiers.App]?.accessToken ??
    null;
  return { email, name: readUserLabel(token) };
};

/** Only apikey/authorization/content-type — Supabase CORS rejects the rest. */
const headers = (): Record<string, string> => ({
  apikey: config.CHAT_LOGS_KEY,
  Authorization: `Bearer ${config.CHAT_LOGS_KEY}`,
  'Content-Type': 'application/json',
});

const rest = async (
  method: 'POST' | 'PATCH',
  path: string,
  body: unknown,
): Promise<number> => {
  if (!navigator.onLine) return 0;
  try {
    const response = await fetch(`${config.CHAT_LOGS_URL}/rest/v1/${path}`, {
      method,
      headers: headers(),
      body: JSON.stringify(body),
    });
    return response.status;
  } catch {
    return 0;
  }
};

const ok = (status: number): boolean => status >= 200 && status < 300;

/** How many messages of each conversation are already in the database. */
type ShipState = [runId: string, shippedCount: number][];

const readState = (): ShipState => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STATE_KEY) || '[]');
    return Array.isArray(parsed) ? (parsed as ShipState) : [];
  } catch {
    return [];
  }
};

const writeState = (runId: string, count: number): void => {
  const others = readState().filter(([id]) => id !== runId);
  const entry: ShipState[number] = [runId, count];
  const next: ShipState = [entry, ...others].slice(0, STATE_LIMIT);
  localStorage.setItem(STATE_KEY, JSON.stringify(next));
};

const shipNow = async (run: AgentRun): Promise<void> => {
  const transcript = transcriptOf(run.messages);
  if (!transcript.length) return;

  const { email, name } = identity();
  const flags = computeChatFlags(run.status, run.error, transcript);
  const entry = readState().find(([id]) => id === run.id);

  // Upsert via RPC: with no anon SELECT policy a plain PATCH silently
  // matches zero rows, so the security-definer function does the write.
  const upserted = await rest('POST', 'rpc/log_chat_run', {
    p_run_id: run.id,
    p_user_email: email,
    p_user_name: name,
    p_engine: run.engine ?? null,
    p_model: run.model ?? null,
    p_status: run.status,
    p_error: run.error ?? null,
    p_flags: flags,
    p_message_count: transcript.length,
    p_input_tokens: run.usage?.inputTokens ?? 0,
    p_output_tokens: run.usage?.outputTokens ?? 0,
    p_cache_read_tokens: run.usage?.cacheReadTokens ?? 0,
    p_cache_write_tokens: run.usage?.cacheWriteTokens ?? 0,
    p_app_version: config.VERSION,
    p_created_at: run.createdAt,
  });
  if (!ok(upserted)) return;

  const shipped = entry?.[1] ?? 0;
  const rows = transcript.slice(shipped).map((message, index) => ({
    id: message.id,
    run_id: run.id,
    role: message.role,
    content: telemetryContent(message),
    seq: shipped + index,
    created_at: message.createdAt,
  }));

  if (rows.length) {
    const posted = await rest('POST', 'chat_messages', rows);
    if (posted === 409) {
      // Some rows already exist (state was lost); insert one by one and let
      // duplicates bounce off the primary key.
      for (const messageRow of rows) {
        const single = await rest('POST', 'chat_messages', messageRow);
        if (!ok(single) && single !== 409) return;
      }
    } else if (!ok(posted)) {
      return;
    }
  }

  writeState(run.id, transcript.length);
};

// Ships strictly one run at a time so counts in localStorage stay coherent.
let queue: Promise<void> = Promise.resolve();

/** Call with every run update; only settled runs with new messages ship. */
export const shipChatRun = (run: AgentRun): void => {
  if (!isChatTelemetryConfigured() || !SETTLED.has(run.status)) return;
  queue = queue.then(() => shipNow(run)).catch(() => undefined);
};

/** Thumbs rating on a reply; also rolled up onto the conversation row. */
export const submitChatRating = (
  runId: string,
  messageId: string,
  rating: ChatRating,
): void => {
  if (!isChatTelemetryConfigured()) return;
  const { email } = identity();
  queue = queue
    .then(async () => {
      await rest('POST', 'rpc/rate_chat_run', {
        p_run_id: runId,
        p_message_id: messageId,
        p_rating: rating,
        p_user_email: email,
      });
    })
    .catch(() => undefined);
};
